import { useState, useEffect, useCallback } from "react";
import { useHistory, useLocation } from "react-router-dom";
import { jwtDecode, JwtPayload } from "jwt-decode";
import { T } from "@transifex/react";
import { SPECIALIST_HOSTNAME } from "env-vars";
import { preserveQueryParams, getAuthReturnPath } from "helpers";
import {
  Container,
  HeadingContainer,
  Heading,
  Subheading,
  Form,
  ErrorMessage,
  DateInput,
  DateInputLabel,
  DateInputHint,
  SubmitButton,
  InfoMessage,
  InfoIcon,
  InfoText,
  InfoTitle,
  InfoDescription,
  InfoInstructions,
} from "./styles";

interface AppMetadata {
  ciq_id?: string;
  location_id?: string;
}

interface TokenPayload extends JwtPayload {
  email?: string;
  app_metadata?: AppMetadata;
}

type VerificationError = "invalid_dob" | "invalid_token" | "general";

export default function PasswordlessVerify() {
  const history = useHistory();
  const location = useLocation();
  const [dateOfBirth, setDateOfBirth] = useState("");
  const [error, setError] = useState<VerificationError | null>(null);
  const [isExpired, setExpired] = useState(false);
  const [isSubmitting, setSubmitting] = useState(false);
  const [firstName, setFirstName] = useState<string | null>(null);

  // Extract 'pa' parameter (passwordless auth token)
  const urlParams = new URLSearchParams(location.search);
  const token = urlParams.get("pa");
  const survey_kind = urlParams.get("kind");

  useEffect(() => {
    // If no token, redirect to regular login
    if (!token) {
      history.push("/login/passwordless");
      return;
    }

    const validateToken = async () => {
      try {
        const response = await fetch(
          `${SPECIALIST_HOSTNAME}/api/v1/authentication/validate_magic_link`,
          {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ pa: token }),
          }
        );

        if (response.status === 404) {
          history.push("/login/passwordless");
          return;
        }

        if (response.ok) {
          const data = await response.json();
          setFirstName(data.first_name);
        } else {
          // For other errors, also redirect
          history.push("/login/passwordless");
        }
      } catch (err) {
        console.warn("Failed to validate magic link", err);
        history.push("/login/passwordless");
      }
    };

    validateToken();
  }, [token, history]);

  // Format date to YYYY-MM-DD
  const formatDateForAPI = (input: string): string | null => {
    // Remove all non-digit characters
    const cleaned = input.replace(/\D/g, "");

    // Try to parse different formats
    if (cleaned.length === 8) {
      // MMDDYYYY or YYYYMMDD
      const year1 = cleaned.substring(0, 4);
      const year2 = cleaned.substring(4, 8);

      // If first 4 chars look like a year (19xx or 20xx)
      if (year1.startsWith("19") || year1.startsWith("20")) {
        const month = cleaned.substring(4, 6);
        const day = cleaned.substring(6, 8);
        return `${year1}-${month}-${day}`;
      }
      // Otherwise assume MMDDYYYY
      else {
        const month = cleaned.substring(0, 2);
        const day = cleaned.substring(2, 4);
        return `${year2}-${month}-${day}`;
      }
    }

    return null;
  };

  const handleDateChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const input = e.target.value;

      // If user typed a slash, pad previous segment
      if (input.endsWith("/")) {
        const parts = input.slice(0, -1).split("/");
        const lastPart = parts[parts.length - 1];
        if (lastPart.length === 1) {
          parts[parts.length - 1] = "0" + lastPart;
        }
        if (parts.length < 3) {
          setDateOfBirth(parts.join("/") + "/");
        }
        return;
      }

      // Strip non-digits and auto-insert slashes
      const digits = input.replace(/\D/g, "").slice(0, 8);
      let formatted = digits;
      if (digits.length > 4) {
        formatted = `${digits.slice(0, 2)}/${digits.slice(2, 4)}/${digits.slice(
          4
        )}`;
      } else if (digits.length > 2) {
        formatted = `${digits.slice(0, 2)}/${digits.slice(2)}`;
      }
      setDateOfBirth(formatted);
    },
    []
  );

  const handleSubmit = useCallback(
    async (e: React.FormEvent) => {
      e.preventDefault();
      setSubmitting(true);
      setError(null);

      // Format the date for the API
      const formattedDate = formatDateForAPI(dateOfBirth);

      if (!formattedDate) {
        setError("general");
        setSubmitting(false);
        return;
      }

      try {
        const response = await fetch(
          `${SPECIALIST_HOSTNAME}/api/v1/authentication/verify_magic_link`,
          {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
              pa: token,
              date_of_birth: formattedDate,
              survey_kind: survey_kind,
            }),
          }
        );

        if (response.ok) {
          const data = await response.json();

          // Store authentication data (same as Auth0 flow)
          const accessToken = data.access_token;
          localStorage.setItem("id_token", accessToken);

          // Calculate expiry (assuming 5 minutes from response or expires_in field)
          const expiryTime = Date.now() + (data.expires_in || 300) * 1000;
          localStorage.setItem("id_token_exp", expiryTime.toString());

          // Decode JWT to extract patient identifiers from app_metadata
          try {
            const decoded = jwtDecode<TokenPayload>(accessToken);
            const appMetadata = decoded.app_metadata;

            // Extract ciq_id (person_id) from app_metadata
            if (appMetadata?.ciq_id) {
              localStorage.setItem("ciq_id", appMetadata.ciq_id);
            }

            // Extract location_id from app_metadata
            if (appMetadata?.location_id) {
              localStorage.setItem("location_id", appMetadata.location_id);
            }

            // Extract email from token payload
            if (decoded.email) {
              localStorage.setItem("ciq_email", decoded.email);
            }
          } catch (decodeError) {
            console.error("Error decoding access token:", decodeError);
            // Fall back to API response if token decode fails
            if (data.person_id) {
              localStorage.setItem("ciq_id", data.person_id);
            }
            if (data.location_id) {
              localStorage.setItem("location_id", data.location_id);
            }
            if (data.email) {
              localStorage.setItem("ciq_email", data.email);
            }
          }

          // Mark as passwordless authenticated (for token refresh logic)
          sessionStorage.setItem("auth_method", "passwordless");
          sessionStorage.setItem("pa_token", token || "");
          sessionStorage.setItem("pa_dob", dateOfBirth); // Cache for auto-refresh
          sessionStorage.setItem("kind", survey_kind || "");

          // Store survey kind in localStorage so SurveyProvider can find it
          if (survey_kind) {
            localStorage.setItem("launch_survey_kind", String(survey_kind));
          }
          // Clear EHR session state on fresh login
          sessionStorage.removeItem("ehr_connected");
          sessionStorage.removeItem("ehr_skipped");
          sessionStorage.removeItem("ehr_flow_complete");

          // Redirect to main app or deeplink path
          const returnPath = getAuthReturnPath();
          history.push(returnPath || preserveQueryParams("/"));
        } else {
          const errorData = await response.json();

          // Map error types from backend
          if (errorData.error === "expired") {
            setExpired(true);
            setError(null); // Not an error, just expired
          } else if (errorData.error === "invalid_dob") {
            setError("invalid_dob");
          } else if (errorData.error === "invalid_token") {
            setError("invalid_token");
          } else {
            setError("general");
          }
        }
      } catch (err) {
        console.error("Verification error:", err);
        setError("general");
      } finally {
        setSubmitting(false);
      }
    },
    [token, dateOfBirth, history]
  );

  const errorMessages: Record<VerificationError, string> = {
    invalid_dob:
      "The date of birth you entered doesn't match our records. Please try again.",
    invalid_token:
      "This link is invalid. Please use the link from your most recent email.",
    general: "An error occurred. Please try again or contact support.",
  };

  // If link is expired, show explanation instead of form
  if (isExpired) {
    return (
      <Container>
        <HeadingContainer>
          <Heading>Link Expired</Heading>
        </HeadingContainer>

        <InfoMessage className="notranslate">
          <InfoIcon>ℹ️</InfoIcon>
          <InfoText>
            <InfoTitle>
              <T _str="This verification link has expired" />
            </InfoTitle>
            <InfoDescription>
              <T _str="For your security, verification links expire after 48 hours. We've automatically sent a new link to your email address and phone number (if provided)." />
            </InfoDescription>
            <InfoInstructions>
              <T
                _str="Please check your email or text messages for the new link and try again or {link}"
                link={
                  <a
                    href="/login/password"
                    style={{ textDecoration: "underline" }}
                  >
                    <T _str="use a username and password" />
                  </a>
                }
              />
              .
            </InfoInstructions>
          </InfoText>
        </InfoMessage>
      </Container>
    );
  }

  return (
    <Container>
      <HeadingContainer className="notranslate">
        <Heading>
          <T _str="Verify Your Identity" />
        </Heading>
        <Subheading className="notranslate">
          <T
            _str="Confirm information for {firstName} or {link}"
            firstName={firstName}
            link={
              <a href="/login/password" style={{ textDecoration: "underline" }}>
                <T _str="use a username and password" />
              </a>
            }
          />
        </Subheading>
      </HeadingContainer>

      <Form onSubmit={handleSubmit}>
        <div>
          <DateInputLabel htmlFor="dob-input">Date of Birth</DateInputLabel>
          <DateInput
            id="dob-input"
            type="text"
            placeholder="MM/DD/YYYY"
            value={dateOfBirth}
            onChange={handleDateChange}
            disabled={isSubmitting}
            required
            maxLength={10}
            aria-label="Date of Birth"
            autoComplete="bday"
          />
          <DateInputHint>Enter as MM/DD/YYYY or MMDDYYYY</DateInputHint>
        </div>

        {error && (
          <ErrorMessage role="alert">{errorMessages[error]}</ErrorMessage>
        )}

        <SubmitButton
          type="submit"
          disabled={isSubmitting || !dateOfBirth || dateOfBirth.length < 8}
        >
          {isSubmitting ? "Verifying..." : "Verify"}
        </SubmitButton>
      </Form>
    </Container>
  );
}
